/**
 * Canonical RBAC role registry — Wave 3 (Janus B2B SaaS RBAC sanitization).
 *
 * Single source of truth for the 10 client roles per governance docx:
 *   - Janus internal: master_admin, super_admin
 *   - Account-scoped: account_owner
 *   - Tenant admin:   tenant_admin
 *   - Executive seat: diretor, gestor, executivo
 *   - Sales seat:     bdr, sdr, mkt
 *
 * Legacy strings (admin / owner / member / director / executive / agent) are
 * mapped through `LEGACY_ROLE_ALIASES` so existing JWTs and persisted user
 * objects keep working while the backend rolls forward.
 */

export const LEGACY_ROLE_ALIASES: Record<string, string> = {
  admin: 'tenant_admin',
  owner: 'tenant_admin',
  member: 'bdr',
  director: 'diretor',
  executive: 'executivo',
  agent: 'mkt',
};

export const CANONICAL_ROLES = [
  'master_admin',
  'super_admin',
  'account_owner',
  'tenant_admin',
  'diretor',
  'gestor',
  'executivo',
  'bdr',
  'sdr',
  'mkt',
] as const;

export type CanonicalRole = (typeof CANONICAL_ROLES)[number];

export const PLATFORM_ADMIN_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'master_admin',
  'super_admin',
]);
export const ACCOUNT_SCOPED_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'account_owner',
]);
export const TENANT_ADMIN_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'tenant_admin',
]);
export const ADMIN_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'master_admin',
  'super_admin',
  'account_owner',
  'tenant_admin',
]);
export const SEAT_EXECUTIVE_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'diretor',
  'gestor',
  'executivo',
]);
export const SEAT_SALES_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'bdr',
  'sdr',
  'mkt',
]);
export const MANAGER_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'diretor',
  'gestor',
]);
/** bdr + sdr share the unified /sales-rep workbench (per docx). mkt is separate. */
export const SALES_REP_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>(['bdr', 'sdr']);

// User-facing labels — short, business-y, no jargon. Used in the header,
// profile, RBAC tags, role pickers. Internal/janus admins keep the
// "Admin" suffix; client roles are clean Portuguese titles.
export const ROLE_DISPLAY_PT_BR: Record<CanonicalRole, string> = {
  master_admin: 'Master Admin',
  super_admin: 'Super Admin',
  account_owner: 'Proprietário',
  tenant_admin: 'Administrador',
  diretor: 'Diretor',
  gestor: 'Gerente',
  executivo: 'Executivo',
  bdr: 'BDR',
  sdr: 'SDR',
  mkt: 'Marketing',
};

export const ROLE_DISPLAY_EN_US: Record<CanonicalRole, string> = {
  master_admin: 'Master Admin (Janus)',
  super_admin: 'Super Admin (Janus)',
  account_owner: 'Account Owner',
  tenant_admin: 'Tenant Admin',
  diretor: 'Director',
  gestor: 'Manager',
  executivo: 'Executive',
  bdr: 'BDR — Prospecting',
  sdr: 'SDR — Qualification',
  mkt: 'MKT — Marketing',
};

export function canonicalizeRole(role: string | null | undefined): CanonicalRole | null {
  if (!role) return null;
  const c = LEGACY_ROLE_ALIASES[role] ?? role;
  return (CANONICAL_ROLES as readonly string[]).includes(c) ? (c as CanonicalRole) : null;
}

export function canonicalizeRoles(
  roles: (string | null | undefined)[] | null | undefined,
): CanonicalRole[] {
  if (!roles) return [];
  const out: CanonicalRole[] = [];
  const seen = new Set<CanonicalRole>();
  for (const r of roles) {
    const c = canonicalizeRole(r);
    if (c && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  return out;
}

export function hasAnyRole(
  roles: (string | null | undefined)[] | undefined,
  allowed: ReadonlySet<CanonicalRole>,
): boolean {
  if (!roles) return false;
  for (const r of roles) {
    const c = canonicalizeRole(r);
    if (c && allowed.has(c)) return true;
  }
  return false;
}

/**
 * True when the user has master_admin or super_admin (Janus internal) — the
 * roles that bypass tenant- and account-scoped guards (gap #2 fix).
 */
export function isPlatformAdmin(
  roles: (string | null | undefined)[] | string | null | undefined,
): boolean {
  if (Array.isArray(roles)) return hasAnyRole(roles, PLATFORM_ADMIN_ROLES);
  return hasAnyRole(roles ? [roles] : [], PLATFORM_ADMIN_ROLES);
}

/** True for {bdr, sdr} — both render the unified /sales-rep workbench. */
export function isSalesRep(role: string | null | undefined): boolean {
  const c = canonicalizeRole(role);
  return !!c && SALES_REP_ROLES.has(c);
}

export function displayRolePtBr(role: string | null | undefined): string {
  const c = canonicalizeRole(role);
  return c ? ROLE_DISPLAY_PT_BR[c] : (role ?? '—');
}

export function displayRoleEnUs(role: string | null | undefined): string {
  const c = canonicalizeRole(role);
  return c ? ROLE_DISPLAY_EN_US[c] : (role ?? '—');
}
